import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../contexts/AuthContext";
import { verifyAdminAccess } from "./api";

function hasAdminRole(roles?: string[]): boolean {
  return Array.isArray(roles) && roles.includes("admin");
}

export function AdminGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, signInWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let alive = true;
    setChecked(false);

    const run = async () => {
      if (!isAuthenticated) {
        if (alive) {
          setAllowed(false);
          setChecked(true);
        }
        return;
      }

      if (hasAdminRole(user.roles)) {
        if (alive) {
          setAllowed(true);
          setChecked(true);
        }
        return;
      }

      const ok = await verifyAdminAccess();
      if (alive) {
        setAllowed(ok);
        setChecked(true);
      }
    };

    void run();
    return () => {
      alive = false;
    };
  }, [isAuthenticated, user.roles, user.id]);

  if (!checked) {
    return (
      <div className="admin-gate">
        <p>Checking admin access…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-gate">
        <h1>Admin sign-in</h1>
        <p>Sign in with your Google admin account to continue.</p>
        <GoogleLogin
          onSuccess={async (resp) => {
            if (!resp.credential) return;
            sessionStorage.setItem("postLoginRedirect", "/admin/dashboard");
            await signInWithGoogle(resp.credential);
            navigate("/admin/dashboard", { replace: true });
          }}
          onError={() => undefined}
        />
        <p className="admin-gate-back">
          <Link to="/start">← Back to site</Link>
        </p>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="admin-gate">
        <h1>Access denied</h1>
        <p>Your account does not have admin permissions.</p>
        <p className="admin-gate-back">
          <Link to="/start">← Back to site</Link>
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
